/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ISitePickerViewProps } from '@msdyn365-commerce-modules/site-picker';
import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import * as React from 'react';

import { ISiteOptionsData, ISitePickerConfig } from '../../master/definition-extensions/site-picker.ext.props.autogenerated';

interface IExtendedSitePickerViewProps extends Omit<ISitePickerViewProps, 'config'> {
    store: IModuleProps;
    config: ISitePickerConfig;
}

type ICurrentLocation = Pick<URL, 'pathname' | 'search' | 'hash'>;

export default (props: IExtendedSitePickerViewProps) => {
    return <>{_renderSitePicker(props)}</>;
};

const _renderDropdownView = (props: IExtendedSitePickerViewProps, currentLanguage?: ISiteOptionsData, showShortSiteName?: boolean) => {
    const { SitePickerMenu, SitePickerMenuSpan } = props;
    //show EN fallback if curentLanguage.shortSiteName is undefined or empty - case if / is the current site path
    return (
        <>
            <Node {...SitePickerMenu} onClick={props.renderSiteOptions} suppressHydrationWarning>
                {showShortSiteName ? currentLanguage?.shortSiteName || 'EN' : currentLanguage?.siteName}
                <Node {...SitePickerMenuSpan} />
            </Node>
            {props.siteOptionsState ? _renderSiteOptionsList(props) : null}
        </>
    );
};

const _getCurrentLocation = (requestContext: MsDyn365.IRequestContext): ICurrentLocation => {
    const { pathname, search, hash } = MsDyn365.msdyn365Commerce.isBrowser ? window.location : requestContext.url.requestUrl;
    return { pathname, search, hash };
};

const _toSitePath = (destinationUrl?: string): string | undefined => {
    if (!destinationUrl || !destinationUrl.startsWith('/') || destinationUrl.startsWith('//')) {
        return undefined;
    }

    return destinationUrl.replace(/\/+$/, '').toLowerCase();
};

//Checks whether a pathname lies inside a site path. The root site ('') contains every page.
const _isWithinSitePath = (pathname: string, sitePath: string): boolean => {
    const normalizedPathname = pathname.replace(/\/+$/, '').toLowerCase();
    return sitePath.length === 0 || normalizedPathname === sitePath || normalizedPathname.startsWith(`${sitePath}/`);
};

const _getCurrentSitePath = (siteOptions: ISiteOptionsData[] | undefined, pathname: string): string => {
    const matches = (siteOptions ?? [])
        .map(option => _toSitePath(option?.siteRedirectUrl?.destinationUrl))
        .filter((sitePath): sitePath is string => sitePath !== undefined && _isWithinSitePath(pathname, sitePath))
        .sort((first, second) => second.length - first.length);

    return matches[0] ?? '';
};

const _buildCurrentPageUrl = (destinationUrl: string, currentSitePath: string, location: ICurrentLocation): string => {
    if (_toSitePath(destinationUrl) === undefined) {
        return destinationUrl;
    }

    const targetSitePath = destinationUrl.replace(/\/+$/, '');
    const pagePath = location.pathname.slice(currentSitePath.length);
    const targetPath = `${targetSitePath}${pagePath}`.replace(/\/$/, '') || '/';

    return `${targetPath}${location.search}${location.hash}`;
};

// Replaces a site option link's href with the current page on the target site
const _withCurrentPageHref = (
    item: React.ReactNode,
    keepCurrentPage: boolean | undefined,
    currentSitePath: string,
    location: ICurrentLocation
): React.ReactNode => {
    if (!keepCurrentPage || !React.isValidElement<{ href?: string }>(item) || !item.props.href) {
        return item;
    }

    return React.cloneElement(item, { href: _buildCurrentPageUrl(item.props.href, currentSitePath, location) });
};

const _renderSiteOptionsList = (props: IExtendedSitePickerViewProps): React.ReactNode => {
    const { SitePickerMenuList, SitePickerMenuListItem, SitePickerMenuListItemArray } = props;
    const location = _getCurrentLocation(props.context.request);
    const currentSitePath = _getCurrentSitePath(props.config.siteOptions, location.pathname);

    const options = SitePickerMenuListItemArray!
        // Hide the site the visitor is already on.
        .filter(item => !React.isValidElement<{ href?: string }>(item) || _toSitePath(item.props.href) !== currentSitePath)
        .map((item, index) => (
            <Node {...SitePickerMenuListItem} key={index}>
                {_withCurrentPageHref(item, props.config.keepCurrentPageOnSiteSwitch, currentSitePath, location)}
            </Node>
        ));

    return (
        <Node {...SitePickerMenuList} onMouseLeave={props.resetSiteOptionsState}>
            {options}
        </Node>
    );
};

const _renderSitePicker = (props: IExtendedSitePickerViewProps): React.ReactNode => {
    // Extends the original site picker behavior
    // Checks for current site path value and evaluates set language
    const { sitePath } = props.store.requestContext;
    const siteOptions = props.config.siteOptions as ISiteOptionsData[];
    const { showShortSiteNameLargeScreen } = props.config;

    const defaultLanguage: ISiteOptionsData = {
        siteRedirectUrl: { destinationUrl: sitePath },
        shortSiteName: 'EN',
        siteName: 'Global (English)'
    };

    const findDefaultLanguage = (siteOptions?.find(o => o.shortSiteName === 'EN') ?? defaultLanguage) as ISiteOptionsData;

    const currentSitePath = _getCurrentSitePath(siteOptions, _getCurrentLocation(props.context.request).pathname);

    const currentLanguage: ISiteOptionsData =
        siteOptions?.find(
            (option): option is ISiteOptionsData => _toSitePath(option?.siteRedirectUrl?.destinationUrl) === currentSitePath
        ) ?? findDefaultLanguage;

    const { isMobileView, SitePicker } = props;
    const showShortSiteName = showShortSiteNameLargeScreen || isMobileView;

    return <Module {...SitePicker}>{_renderDropdownView(props, currentLanguage, showShortSiteName)}</Module>;
};
